import {t} from 'i18next';
import React, {useCallback, useRef, useState} from 'react';
import {Icon} from '@iconify/react';
import {Item, Menu, RightSlot, Separator} from 'react-contexify';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';
import HighlightedInputMedia from '../../../components/fileLoader/input.media';
import useNblFileReader from '../../../components/fileLoader/util/useNblFileReader';
import ConfirmModal from '../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {MENU_IDS} from '../../../constants/menuIds';
import {setSidePanelContentAction} from '../../../store/actions/grid';
import {SidePanelContent} from '../../../store/actions/types/grid.types';
import useElementActions from '../../../utils/hooks/slideUpdates/useElementActions';
import {hasValidClipboardDataSelector} from '../../../store/selectors/clipboard.selectors';
import {SlideElementHotkeys} from '../../../constants/slideElementHotkeys';
import {isSlideModifiedSelector, selectedSlideIndexSelector, selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import {updateSelectedSlideAction} from '../../../store/actions/slide';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import {createKeyMatcher, formatShortcuts} from '../../../incase-domain/components/contextMenu/ContextMenu';
import createSlideTemplate from '../../../service/template/template';
import {SLIDE_LAYOUT_TEMPLATE_OPTIONS} from '../../../service/template/slideTemplateOptions';
import {TEMPLATE} from '../../../service/template/types';

export function SlideContextMenuPortal(): JSX.Element {
    const dispatch = useDispatch();
    const selectedSlideIndex = useSelector(selectedSlideIndexSelector);
    const modified = useSelector(isSlideModifiedSelector);
    const user = useSelector(loggedInUserSelector);
    const {importElement, pasteElement} = useElementActions();
    const hasValidClipboardData = useSelector(hasValidClipboardDataSelector);
    const selectedPresentation = useSelector(selectedPresentationSelector);
    const selectedSlide = useSelector(selectedSlideSelector);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [discardModal, setDiscardModal] = useState(false);
    const isSelectedSlideEmpty = selectedSlide.elements.length === 0;

    const handleElementImport = (): void => {
        fileInputRef.current?.click();
    };

    const getSlide = useCallback(() => selectedSlide, [selectedSlide]);
    const nblFileReader = useNblFileReader((elements) => {
        elements.forEach(importElement);
    }, getSlide);

    const showSlideSettings = (): void => {
        dispatch(setSidePanelContentAction(SidePanelContent.SLIDE));
    };

    const resetSlide = useCallback((): void => {
        dispatch(setSidePanelContentAction(SidePanelContent.ELEMENTS));
        dispatch(updateSelectedSlideAction(selectedPresentation.slides[selectedSlideIndex], selectedSlideIndex, true));
    }, [dispatch, selectedPresentation.slides, selectedSlideIndex]);

    const applyTemplate = useCallback(
        (template: TEMPLATE): void => {
            dispatch(
                updateSelectedSlideAction(
                    {
                        ...selectedSlide,
                        elements: createSlideTemplate(template, selectedSlide._id),
                    },
                    selectedSlideIndex,
                    true
                )
            );
        },
        [dispatch, selectedSlide, selectedSlideIndex]
    );

    const hideDiscardWhenAutoSave = !!user?.autoSave;

    return (
        <>
            <Menu id={MENU_IDS.SLIDE}>
                {SLIDE_LAYOUT_TEMPLATE_OPTIONS.map((item) => (
                    <Item key={item.template} hidden={() => !isSelectedSlideEmpty} onClick={() => applyTemplate(item.template)}>
                        <Icon className={classNames('contexify_itemContent__icon')} icon={item.icon} />
                        <span className="contexify_itemContent__label">{t(item.labelKey)}</span>
                        <RightSlot>{null}</RightSlot>
                    </Item>
                ))}
                <Separator hidden={() => !isSelectedSlideEmpty} />
                <Item
                    disabled={!hasValidClipboardData}
                    onClick={() => pasteElement()}
                    keyMatcher={createKeyMatcher(SlideElementHotkeys.Paste.getPlatformKeys(window.navigator))}
                >
                    <Icon className={classNames('contexify_itemContent__icon')} icon="solar:clipboard-linear" />
                    <span className="contexify_itemContent__label">{t('contextMenuElement.paste')}</span>
                    <RightSlot>{formatShortcuts(SlideElementHotkeys.Paste.getPlatformKeys(window.navigator))}</RightSlot>
                </Item>
                <Item
                    onClick={handleElementImport}
                    keyMatcher={createKeyMatcher(SlideElementHotkeys.Import.getPlatformKeys(window.navigator))}
                >
                    <Icon className={classNames('contexify_itemContent__icon')} icon="solar:upload-minimalistic-linear" />
                    <span className="contexify_itemContent__label">{t('contextMenuSlide.importElement')}</span>
                    <RightSlot>{formatShortcuts(SlideElementHotkeys.Import.getPlatformKeys(window.navigator))}</RightSlot>
                </Item>
                <Separator />
                <Item hidden={() => hideDiscardWhenAutoSave} disabled={!modified} onClick={() => setDiscardModal(true)}>
                    <Icon className={classNames('contexify_itemContent__icon')} icon="solar:undo-left-round-linear" />
                    <span className="contexify_itemContent__label">{t('contextMenuSlide.discard')}</span>
                    <RightSlot>{null}</RightSlot>
                </Item>
                <Item
                    onClick={showSlideSettings}
                    keyMatcher={createKeyMatcher(SlideElementHotkeys.SidepanelSlideSettings.getPlatformKeys(window.navigator))}
                >
                    <Icon className={classNames('contexify_itemContent__icon')} icon="solar:settings-linear" />
                    <span className="contexify_itemContent__label">{t('contextMenuSlide.settings')}</span>
                    <RightSlot>{formatShortcuts(SlideElementHotkeys.SidepanelSlideSettings.getPlatformKeys(window.navigator))}</RightSlot>
                </Item>
            </Menu>

            <ConfirmModal
                color="warning"
                message={t('contextMenuSlide.discardModal.message')}
                isOpen={discardModal}
                onCancel={() => setDiscardModal(false)}
                onConfirm={() => {
                    resetSlide();
                    setDiscardModal(false);
                }}
                showCancel
                showConfirm
                cancelText={`${t('common.cancel')}`}
                confirmText={`${t('common.confirm')}`}
            />

            <div className="u-d-none">
                <HighlightedInputMedia inputRef={fileInputRef} onLoad={nblFileReader} type="nbl" />
            </div>
        </>
    );
}
